import React, { useRef, useState } from 'react';
import { Modal, Collapse, Carousel, Tooltip } from 'antd';
import { fakeProImage } from '../../config/fakeData';
import { InfoCircleOutlined } from '@ant-design/icons';
import { ModalCarouselWrapper } from "./style";

const AMAZON_BASE_URL = "https://www.amazon.in/dp/";

const SLIDE_LABELS = ['Performance', 'Cost', 'Profit'];

const ProductDetailsModal = ({ open, onClose, data, currencySign }) => {
    const getAmount = (val) => {
        const amount = parseFloat(val || 0);
        return `${amount < 0 ? '-' : ''}${currencySign}${Math.abs(amount).toFixed(2)}`;
    };

    const mainCollapseRef = useRef(null);
    const carouselRef     = useRef(null);
    const [currentSlide, setCurrentSlide] = useState(0);

    const handleCollapseChange = (activeKeys) => {
        if (Array.isArray(activeKeys) && activeKeys.length > 0) {
            const newlyOpenedKey = activeKeys[activeKeys.length - 1];
            const panelHeader = mainCollapseRef.current?.querySelector(
                `.ant-collapse-item[data-key="${newlyOpenedKey}"] .ant-collapse-header`
            );

            if (panelHeader) {
                panelHeader.scrollIntoView({
                    behavior: 'smooth',
                    block: 'end',
                    inline: 'nearest'
                });
            }
        }
    };

    const generateMetricsCollapseItems = (metricsData) => {
        if (!metricsData) return { costMetrics: [], profitMetrics: [], performanceMetrics: [] };

        const costMetrics = [
            {
                key: '1',
                title: 'Amazon Fees',
                amount: getAmount(metricsData.more_data.amazon_fees.total),
                items: Object.entries(metricsData.more_data.amazon_fees)
                    .filter(([key]) => key !== 'total')
                    .map(([key, value]) => ({
                        label: key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
                        value: getAmount(value)
                    }))
            },
            {
                key: '2',
                title: 'Advertising Cost',
                amount: getAmount(-metricsData.more_data.advertising_cost.total),
                items: [
                    { label: 'Sponsored Product', value: getAmount(-metricsData.more_data.advertising_cost.sp_cost) },
                    { label: 'Sponsored Brand', value: getAmount(-metricsData.more_data.advertising_cost.sb_cost) },
                    { label: 'Sponsored Display', value: getAmount(-metricsData.more_data.advertising_cost.sd_cost) }
                ]
            },
            {
                key: '3',
                title: 'Promo',
                amount: getAmount(metricsData.more_data.promo),
                items: []
            },
            {
                key: '4',
                title: 'Gift wrap',
                amount: getAmount(-metricsData.more_data.giftwrap),
                items: []
            },
            {
                key: '5',
                title: 'Shipping Cost',
                amount: getAmount(-metricsData.more_data.shipping_cost.total),
                items: []
            },
            {
                key: '6',
                title: (
                    <div className="d-flex align-items-center">
                        Refund cost
                        {!!metricsData.more_data.refund_cost.total && (
                            <Tooltip
                                placement="top"
                                title={
                                    <div className="p-1">
                                        <div className="d-flex justify-content-between mb-1" style={{ minWidth: '160px' }}>
                                            <span>Refunded amount:</span>
                                            <span className="fw-bold ms-4">{getAmount(metricsData.more_data.refund_cost.refunded_amount)}</span>
                                        </div>
                                        <div className="d-flex justify-content-between mb-1">
                                            <span>Fixed closing fee:</span>
                                            <span className="fw-bold ms-4">{getAmount(metricsData.more_data.refund_cost.fixed_closing_fee)}</span>
                                        </div>
                                        <div className="d-flex justify-content-between">
                                            <span>Ship promotion:</span>
                                            <span className="fw-bold ms-4">{getAmount(metricsData.more_data.refund_cost.ship_promotion)}</span>
                                        </div>
                                    </div>
                                }
                            >
                                <InfoCircleOutlined className="ms-2 text-gray-500" style={{ fontSize: '13px', cursor: 'help' }} />
                            </Tooltip>
                        )}
                    </div>
                ),
                amount: getAmount(metricsData.more_data.refund_cost.total),
                items: []
            },
            {
                key: '7',
                title: 'Cost of Goods',
                amount: getAmount(-metricsData.more_data.cost_of_goods.total),
                items: [{ label: 'COGS', value: getAmount(-metricsData.more_data.cost_of_goods.cost_of_goods_sold) }]
            },
            {
                key: '8',
                title: 'Expenses',
                amount: getAmount(-metricsData.more_data.expenses.total),
                items: Object.entries(metricsData.more_data.expenses)
                    .filter(([key]) => key !== 'total')
                    .map(([key, value]) => ({
                        label: key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
                        value: getAmount(-value)
                    }))
            },
            {
                key: '9',
                title: 'Net Tax',
                amount: getAmount(metricsData.more_data.tax_liability.total),
                items: [
                    { label: 'Tax Collected', value: getAmount(metricsData.more_data.tax_liability.total_tax_collected) },
                    {
                        label: (
                            <div className="d-flex align-items-center">
                                Tax Paid
                                {metricsData.more_data.tax_liability.tax_paid_breakdown && (
                                    <Tooltip
                                        placement="top"
                                        title={
                                            <div className="p-1">
                                                <div className="d-flex justify-content-between mb-1" style={{ minWidth: '160px' }}>
                                                    <span>Tax paid on COGS:</span>
                                                    <span className="fw-bold ms-4">{getAmount(metricsData.more_data.tax_liability.tax_paid_breakdown.cogs)}</span>
                                                </div>
                                                <div className="d-flex justify-content-between mb-1">
                                                    <span>Tax paid on Amazon fee:</span>
                                                    <span className="fw-bold ms-4">{getAmount(metricsData.more_data.tax_liability.tax_paid_breakdown.fees)}</span>
                                                </div>
                                                <div className="d-flex justify-content-between">
                                                    <span>Tax paid on Ads:</span>
                                                    <span className="fw-bold ms-4">{getAmount(metricsData.more_data.tax_liability.tax_paid_breakdown.advertising)}</span>
                                                </div>
                                            </div>
                                        }
                                    >
                                        <InfoCircleOutlined className="ms-2 text-gray-500" style={{ fontSize: '13px', cursor: 'help' }} />
                                    </Tooltip>
                                )}
                            </div>
                        ),
                        value: getAmount(metricsData.more_data.tax_liability.total_tax_paid)
                    }
                ]
            }
        ];

        const profitMetrics = [
            { key: '1', title: 'Gross Profit', amount: getAmount(metricsData.more_data.gross_profit), items: [] },
            { key: '2', title: 'Net Profit', amount: getAmount(metricsData.more_data.net_profit), items: [] }
        ];

        const performanceMetrics = [
            {
                key: '1',
                title: 'Total Sales',
                amount: getAmount(metricsData.more_data.sales.total),
                items: [
                    { label: 'Organic', value: getAmount(metricsData.more_data.sales.organic) },
                    { label: 'Sponsored Product', value: getAmount(metricsData.more_data.sales.sp_sales) },
                    { label: 'Sponsored Brand', value: getAmount(metricsData.more_data.sales.sb_sales) },
                    { label: 'Sponsored Display', value: getAmount(metricsData.more_data.sales.sd_sales) }
                ]
            },
            {
                key: '2',
                title: 'Advertising clicks',
                amount: `${metricsData.more_data.advertising_clicks.total || 0}`,
                items: [
                    { label: 'Sponsored Product', value: `${metricsData.more_data.advertising_clicks.sp_click || 0}` },
                    { label: 'Sponsored Brand', value: `${metricsData.more_data.advertising_clicks.sb_click || 0}` },
                    { label: 'Sponsored Display', value: `${metricsData.more_data.advertising_clicks.sd_click || 0}` },
                ]
            },
            {
                key: '3',
                title: 'Total units',
                amount: `${metricsData.more_data.units.total || 0}`,
                items: [
                    { label: 'Organic', value: `${metricsData.more_data.units.organic || 0}` },
                    { label: 'Sponsored Product', value: `${metricsData.more_data.units.sp_units || 0}` },
                    { label: 'Sponsored Brand', value: `${metricsData.more_data.units.sb_units || 0}` },
                    { label: 'Sponsored Display', value: `${metricsData.more_data.units.sd_units || 0}` },
                ]
            },
            {
                key: '4',
                title: (
                    <div className="d-flex align-items-center">
                        Ad conversion
                        <Tooltip title="(SP Units + SB Units + SD Units) / Total Ad Clicks X 100" placement="top">
                            <InfoCircleOutlined className="ms-2 text-gray-500" style={{ fontSize: '14px', cursor: 'help' }} />
                        </Tooltip>
                    </div>
                ),
                amount: `${metricsData.more_data.advertising_clicks.total > 0
                    ? (
                        ((metricsData.more_data.units.sp_units + metricsData.more_data.units.sb_units + metricsData.more_data.units.sd_units || 0) / metricsData.more_data.advertising_clicks.total) * 100
                    ).toFixed(2)
                    : 0}%`,
                items: []
            },
            { key: '5', title: 'Refund units', amount: `${metricsData.more_data.refund_units}`, items: [] },
            { key: '6', title: 'Refunds %', amount: `${metricsData.more_data.refunds_percent}%`, items: [] }
        ];

        const formatSections = (items) =>
            items.map((section) => ({
                key: section.key,
                label: (
                    <div className="d-flex justify-content-between w-100">
                        <span className="text-gray-800 fw-bold fs-6">
                            {section.title}
                        </span>
                        <span className="text-gray-800 fw-bolder fs-6 me-3 gotham_black">
                            {section.amount}
                        </span>
                    </div>
                ),
                children: section.items.length > 0 ? (
                    <>
                        {section.items.map((item, i) => (
                            <React.Fragment key={i}>
                                <div className="row g-4 mt-0 ps-9">
                                    <div className="col w-md-100 d-flex align-items-center justify-content-between pe-md-0 mt-1">
                                        <h3 className="mb-1 fw-semibold text-gray-700 fs-6 text-dark">{item.label}</h3>
                                        <h3 className="fs-6 text-gray-800 fw-bolder mb-0">{item.value}</h3>
                                    </div>
                                </div>
                                {i < section.items.length - 1 && (
                                    <div className="separator separator-dashed border-gray-300 my-2" />
                                )}
                            </React.Fragment>
                        ))}
                    </>
                ) : null,
                showArrow: section.items.length > 0,
                collapsible: section.items.length > 0 ? '' : 'disabled',
                className: section.items.length === 0 ? 'ant-collapse-item-no-child-padding' : ''
            }));

        return {
            costMetrics: formatSections(costMetrics),
            profitMetrics: formatSections(profitMetrics),
            performanceMetrics: formatSections(performanceMetrics),
        };
    };

    const { costMetrics, profitMetrics, performanceMetrics } = generateMetricsCollapseItems(data);

    return (
        <ModalCarouselWrapper>
            <Modal
                open={open}
                onCancel={onClose}
                footer={null}
                width={700}
                centered
                styles={{
                    body: {
                        height: '85vh',
                        padding: 0,
                        display: 'flex',
                        flexDirection: 'column'
                    }
                }}
            >
                {data && (
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

                        {/* 1. FIXED MODAL HEADER */}
                        <div style={{ padding: '24px 24px 10px 24px', flexShrink: 0 }}>
                            <div style={{ textAlign: 'center', marginBottom: 15 }}>
                                <img src={data?.image_url || fakeProImage} alt="Product" style={{
                                    maxHeight: 100,
                                    objectFit: 'contain',
                                }}/>
                            </div>
                            <h2 style={{ textAlign: 'center', marginBottom: 8, fontSize: '1.1rem' }}>
                                <a
                                    href={data?.asin ? `${AMAZON_BASE_URL}${data.asin}` : "#"}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: '#1890ff', textDecoration: 'none' }}
                                >
                                    {data?.product_title}
                                </a>
                            </h2>
                            <div style={{ textAlign: 'center', marginBottom: 15 }}>
                                <span style={{ color: '#595959', marginRight: 15 }}><b>ASIN:</b> {data?.asin}</span>
                                <span style={{ color: '#8c8c8c' }}><b>SKU:</b> {data?.sku}</span>
                            </div>
                        </div>

                        {/* 2. CENTER SECTION: Tabs + Carousel */}
                        <div style={{ flex: 1, padding: '0 24px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                            {/* Tab navigation — synced with carousel */}
                            <div style={{ display: 'flex', gap: 6, width: '100%', paddingBottom: 2, marginBottom: 12, flexShrink: 0 }}>
                                {SLIDE_LABELS.map((label, i) => {
                                    const isActive = i === currentSlide;
                                    return (
                                        <button
                                            key={label}
                                            onClick={() => carouselRef.current?.goTo(i)}
                                            aria-label={`Show ${label}`}
                                            title={label}
                                            style={{
                                                flex: 1,
                                                padding: '7px 8px',
                                                border: `1.5px solid ${isActive ? '#314F71' : '#e4e6ef'}`,
                                                borderRadius: 8,
                                                background: isActive ? '#314F71' : 'transparent',
                                                color: isActive ? '#ffffff' : '#7e8299',
                                                fontSize: 12.5,
                                                fontWeight: 600,
                                                cursor: 'pointer',
                                                outline: 'none',
                                                whiteSpace: 'nowrap',
                                                textAlign: 'center',
                                                boxShadow: isActive ? '0 3px 10px rgba(49,79,113,0.35)' : 'none',
                                                transition: 'background 0.2s ease, color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
                                            }}
                                        >
                                            {label}
                                        </button>
                                    );
                                })}
                            </div>

                            <Carousel
                                ref={carouselRef}
                                effect="scrollx"
                                className="metrics-modal-carousel"
                                dots={false}
                                afterChange={(index) => setCurrentSlide(index)}
                                style={{ height: '100%' }}
                            >
                                {/* Performance Slide */}
                                <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ overflowY: 'auto', maxHeight: 'calc(85vh - 420px)', scrollbarWidth: 'thin' }}>
                                        <Collapse items={performanceMetrics} bordered={false} onChange={handleCollapseChange} ref={mainCollapseRef} />
                                    </div>
                                </div>

                                {/* Cost Slide */}
                                <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ overflowY: 'auto', maxHeight: 'calc(85vh - 420px)', scrollbarWidth: 'thin' }}>
                                        <Collapse items={costMetrics} bordered={false} onChange={handleCollapseChange} ref={mainCollapseRef} />
                                    </div>
                                </div>

                                {/* Profit Slide */}
                                <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ overflowY: 'auto', maxHeight: 'calc(85vh - 420px)', scrollbarWidth: 'thin' }}>
                                        <Collapse items={profitMetrics} bordered={false} onChange={handleCollapseChange} ref={mainCollapseRef} />
                                    </div>
                                </div>
                            </Carousel>
                        </div>

                        {/* 3. FIXED MODAL FOOTER */}
                        <div style={{ padding: '20px 24px', flexShrink: 0, background: '#fff' }}>
                            <div style={{
                                padding: '12px 20px',
                                border: '1px dashed #63CE94',
                                borderRadius: '8px',
                                background: '#63ce9440',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <h5 style={{ marginBottom: 0, color: '#0f7e3e', fontWeight: 600, fontSize: '1.2rem' }}>Net Profit</h5>
                                <h4 style={{ marginBottom: 0, fontWeight: 800, color: '#0f7e3e', fontSize: '1.8rem' }}>
                                    {getAmount(data?.net_profit)}
                                </h4>
                            </div>
                        </div>
                    </div>
                )}
            </Modal>
        </ModalCarouselWrapper>
    );
};

export default ProductDetailsModal;


